'use client'

import generateClassNames from '@/utils/generate-class-names'
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
  type UseTransitionStatusProps,
} from '@floating-ui/react'
import { forwardRef } from 'react'
import './modal.scss'

const classes = generateClassNames('modal', ['overlay', 'container'])

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  duration?: UseTransitionStatusProps['duration']
  onOpenChange?: (open: boolean) => void
  open?: boolean
}

const Modal = forwardRef(function Modal(
  props: ModalProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) {
  const { children, duration = 250, onOpenChange, open, ...other } = props

  const { refs, context } = useFloating<HTMLDivElement>({
    open,
    onOpenChange,
  })

  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' })
  const role = useRole(context)
  const { getFloatingProps } = useInteractions([dismiss, role])

  const ref = useMergeRefs([refs.setFloating, forwardedRef])

  const { isMounted, status } = useTransitionStatus(context, {
    duration,
  })

  if (!isMounted) {
    return null
  }

  return (
    <FloatingPortal>
      <FloatingOverlay
        className={classes.overlay}
        data-status={status}
        lockScroll
      />
      <FloatingFocusManager context={context}>
        <div className={classes.container} role='presentation'>
          <div ref={ref} data-status={status} {...getFloatingProps(other)}>
            {children}
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
})

export default Modal
